/**
 * Mini Player Component - Floating, always-on-top player
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAudio } from '../contexts/AudioContext';
import './MiniPlayer.css';

const MiniPlayer = () => {
  const {
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    skipToNext,
    skipToPrevious,
    seekTo,
    changeVolume,
    toggleMute,
  } = useAudio();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const handleClose = () => {
    setIsMinimized(true);
  };

  // Dragging functionality
  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newPosition = {
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        };

        // Keep within bounds
        const maxX = window.innerWidth - (isExpanded ? 400 : 300);
        const maxY = window.innerHeight - (isExpanded ? 200 : 80);

        setPosition({
          x: Math.max(0, Math.min(newPosition.x, maxX)),
          y: Math.max(0, Math.min(newPosition.y, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, isExpanded]);

  // Don't render if no song is playing
  if (!currentSong) {
    return null;
  }

  if (isMinimized) {
    return (
      <div
        className="mini-player-minimized"
        onClick={handleRestore}
        style={{ left: position.x, top: position.y }}
      >
        <div className="mini-indicator">
          <div className={`mini-pulse ${isPlaying ? 'playing' : ''}`}></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mini-player ${isExpanded ? 'expanded' : 'collapsed'}`}
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className="mini-header no-drag">
        <div className="mini-title">
          <span className="mini-song-title">{currentSong.title}</span>
          <span className="mini-song-artist">{currentSong.artist}</span>
        </div>
        <div className="mini-controls-header">
          {!isExpanded && (
            <button
              className="mini-btn-expand"
              onClick={handleExpand}
              title="Expand"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
              </svg>
            </button>
          )}
          {isExpanded && (
            <button
              className="mini-btn-collapse"
              onClick={handleCollapse}
              title="Collapse"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
              </svg>
            </button>
          )}
          <button
            className="mini-btn-close"
            onClick={handleClose}
            title="Minimize"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="mini-content no-drag">
          {/* Progress Bar */}
          <div className="mini-progress">
            <span className="mini-time">{formatTime(currentTime)}</span>
            <div
              className="mini-progress-bar"
              onClick={(e) => {
                const rect = e.target.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = x / rect.width;
                seekTo(percentage * duration);
              }}
            >
              <div
                className="mini-progress-fill"
                style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
              />
            </div>
            <span className="mini-time">{formatTime(duration)}</span>
          </div>

          {/* Main Controls */}
          <div className="mini-main-controls">
            <button
              className="mini-control-btn"
              onClick={skipToPrevious}
              title="Previous"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>

            <button
              className="mini-control-btn mini-play-btn"
              onClick={togglePlay}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            <button
              className="mini-control-btn"
              onClick={skipToNext}
              title="Next"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>
          </div>

          {/* Volume Control */}
          <div className="mini-volume">
            <button
              className="mini-volume-btn"
              onClick={toggleMute}
              title="Mute"
            >
              {isMuted || volume === 0 ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              className="mini-volume-slider"
            />
          </div>
        </div>
      )}

      {/* Collapsed state - just basic controls */}
      {!isExpanded && (
        <div className="mini-collapsed no-drag">
          <button
            className="mini-control-btn-collapsed"
            onClick={skipToPrevious}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>

          <button
            className="mini-control-btn-collapsed mini-play-btn-collapsed"
            onClick={togglePlay}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          <button
            className="mini-control-btn-collapsed"
            onClick={skipToNext}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default MiniPlayer;